import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, Check, ChevronLeft, Filter, LoaderCircle, Plus, Trash2, X } from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useSharedQuote } from '../../context/QuoteContext';
import { useOrgServices } from '../../hooks/useOrgServices';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { ReadOnlyBanner } from '../../components/ui/ReadOnlyBanner';
import { OrgServiceItem, QuoteServiceItem } from '../../types/admin';

/** Local Scope of Services state: enabled org service ids + quote-level custom services. */
interface ScopeState {
  enabledIds: Set<string>;
  customServices: QuoteServiceItem[];
}

/** Quiet period after the last tap before the coalesced save is sent. */
const SAVE_DEBOUNCE_MS = 400;

// ── Constants ──────────────────────────────────────────────────────────────

const PARTY_OPTIONS = ['Customer', 'Mover'] as const;
type Party = (typeof PARTY_OPTIONS)[number];

// ── Service toggle row ────────────────────────────────────────────────────────

function ServiceToggleRow({
  service,
  enabled,
  onToggle,
  saving,
}: {
  service: OrgServiceItem;
  enabled: boolean;
  onToggle: () => void;
  saving: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={saving}
      className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border text-left
        transition-all duration-150 active:opacity-80
        ${enabled
          ? 'bg-plum/5 border-plum/20'
          : 'bg-white border-stone hover:border-plum/30'
        }
        ${saving ? 'opacity-60' : ''}`}
    >
      {/* Check circle */}
      <div className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center
        transition-all duration-150
        ${enabled ? 'bg-plum border-plum' : 'border-stone bg-white'}`}
      >
        {enabled && <Check size={11} className="text-white" strokeWidth={3} />}
      </div>

      <p className={`flex-1 min-w-0 text-small truncate transition-colors duration-150
        ${enabled ? 'font-semibold text-plum' : 'font-medium text-midnight'}`}
      >
        {service.code && <span className="text-plum font-bold mr-1.5">{service.code}</span>}
        {service.name}
      </p>

      {service.note && (
        <p className="text-[11px] text-slate shrink-0 max-w-[120px] truncate hidden sm:block">
          {service.note}
        </p>
      )}
    </button>
  );
}

// ── Custom service toggle row (with delete) ─────────────────────────────────

function CustomServiceToggleRow({
  item,
  onToggle,
  onDelete,
  saving,
  isDraft,
}: {
  item: QuoteServiceItem;
  onToggle: () => void;
  onDelete: () => void;
  saving: boolean;
  isDraft: boolean;
}) {
  return (
    <div
      className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border
        transition-all duration-150
        ${item.enabled
          ? 'bg-plum/5 border-plum/20'
          : 'bg-white border-stone'
        }
        ${saving ? 'opacity-60' : ''}`}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={saving}
        className="flex items-center gap-3 flex-1 min-w-0 text-left active:opacity-80"
      >
        <div className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center
          transition-all duration-150
          ${item.enabled ? 'bg-plum border-plum' : 'border-stone bg-white'}`}
        >
          {item.enabled && <Check size={11} className="text-white" strokeWidth={3} />}
        </div>

        <p className={`flex-1 min-w-0 text-small truncate transition-colors duration-150
          ${item.enabled ? 'font-semibold text-plum' : 'font-medium text-midnight'}`}
        >
          {item.code && <span className="text-plum font-bold mr-1.5">{item.code}</span>}
          {item.serviceName}
        </p>
      </button>

      {item.note && (
        <p className="text-[11px] text-slate shrink-0 max-w-[120px] truncate hidden sm:block">
          {item.note}
        </p>
      )}

      {isDraft && (
        <button
          type="button"
          onClick={onDelete}
          disabled={saving}
          className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg
                     text-slate hover:text-rose active:bg-mist
                     transition-colors duration-150"
          aria-label={`Delete ${item.serviceName}`}
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );
}

// ── Add Custom Service Form (bottom sheet / centered dialog) ─────────────────

function AddCustomServiceForm({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (input: { name: string; party: Party; note?: string }) => Promise<unknown>;
}) {
  const [name, setName] = useState('');
  const [party, setParty] = useState<Party>('Customer');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Service name is required.'); return; }
    setError('');
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        party,
        note: note.trim() || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save service.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[92vh] overflow-y-auto
                      md:rounded-2xl md:max-w-md md:w-full md:max-h-[80vh] md:pb-6">
        {/* Drag handle — mobile only */}
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">Add Service</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-rose/10 border border-rose/30">
              <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
              <p className="text-small text-rose">{error}</p>
            </div>
          )}

          {/* Service name */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="csvc-name"
              className="block text-[11px] font-bold uppercase tracking-widest text-slate"
            >
              Service<span className="text-rose ml-0.5">*</span>
            </label>
            <input
              id="csvc-name"
              type="text"
              autoComplete="off"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Furniture Protection"
              className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          {/* Party */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="csvc-party"
              className="block text-[11px] font-bold uppercase tracking-widest text-slate"
            >
              Party<span className="text-rose ml-0.5">*</span>
            </label>
            <div className="flex bg-mist rounded-lg p-1 gap-1">
              {PARTY_OPTIONS.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setParty(p)}
                  className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150
                    ${party === p
                      ? 'bg-white text-midnight shadow-brand-sm'
                      : 'text-slate hover:text-midnight'
                    }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="csvc-note"
              className="block text-[11px] font-bold uppercase tracking-widest text-slate"
            >
              Note
              <span className="text-slate font-normal normal-case tracking-normal ml-1">(optional)</span>
            </label>
            <textarea
              id="csvc-note"
              rows={2}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Optional context…"
              className="w-full px-3 py-2.5 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate resize-none
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="h-11 rounded-lg bg-plum text-white text-[13px] font-semibold
                       flex items-center justify-center gap-2
                       hover:bg-rose active:opacity-80 disabled:opacity-50
                       transition-colors duration-150 mt-1"
          >
            {saving ? 'Saving…' : 'Add Service'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ScopeOfServicesPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const { quote, loading: quoteLoading, updateQuote } = useSharedQuote();
  const { items: orgServices, loading: servicesLoading } = useOrgServices(orgId ?? '');

  const [initialized, setInitialized] = useState(false);
  // Set of enabled service IDs (org services)
  const [enabledIds, setEnabledIds] = useState<Set<string>>(new Set());
  // Custom services added at quote level
  const [customServices, setCustomServices] = useState<QuoteServiceItem[]>([]);
  // Rows whose latest tap has not been confirmed by the server yet.
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [saveError, setSaveError] = useState('');
  // Coalesced save queue: every tap updates the local state immediately; the
  // save runs off the latest state, one request at a time, and re-runs when
  // taps arrive while a request is in flight. Nothing is dropped.
  const latestRef = useRef<ScopeState>({ enabledIds: new Set(), customServices: [] });
  const savedRef = useRef<ScopeState>({ enabledIds: new Set(), customServices: [] });
  const inFlightRef = useRef<Promise<void> | null>(null);
  const dirtyRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingIdsRef = useRef<Set<string>>(new Set());
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEnabledOnly, setShowEnabledOnly] = useState(false);

  // Initialize once both quote and services are loaded
  useEffect(() => {
    if (quote && !servicesLoading && !initialized) {
      const savedItems = quote.scopeOfServices ?? [];
      const ids = new Set<string>();
      const customs: QuoteServiceItem[] = [];
      for (const s of savedItems) {
        if (s.isCustom) {
          customs.push(s);
        } else if (s.enabled) {
          ids.add(s.serviceId);
        }
      }
      setEnabledIds(ids);
      setCustomServices(customs);
      latestRef.current = { enabledIds: ids, customServices: customs };
      savedRef.current = latestRef.current;
      setInitialized(true);
    }
  }, [quote, servicesLoading, initialized]);

  /** Build the full service list payload from current state (filtered by quote type). */
  function buildPayload(nextEnabledIds: Set<string>, nextCustomServices: QuoteServiceItem[]): QuoteServiceItem[] {
    const isRes = quote?.type === 'residential';
    const orgItems = orgServices
      .filter(s => {
        const svcType = s.quoteType ?? 'commercial';
        return isRes ? svcType === 'residential' : svcType === 'commercial';
      })
      .map(s => ({
        serviceId: s.id,
        serviceName: s.name,
        code: s.code,
        party: s.party,
        order: s.order,
        enabled: nextEnabledIds.has(s.id),
      }));
    return [...orgItems, ...nextCustomServices];
  }

  const buildPayloadRef = useRef(buildPayload);
  buildPayloadRef.current = buildPayload;

  /** Send the latest local state, re-running while taps keep arriving. */
  function runSave(): Promise<void> {
    if (inFlightRef.current) {
      dirtyRef.current = true;
      return inFlightRef.current;
    }
    const run = (async () => {
      do {
        dirtyRef.current = false;
        const snapshot = latestRef.current;
        try {
          await updateQuote({ scopeOfServices: buildPayloadRef.current(snapshot.enabledIds, snapshot.customServices) });
          savedRef.current = snapshot;
        } catch (err) {
          setSaveError(err instanceof Error ? err.message : 'Failed to save');
          // Revert every unsaved tap to the last state the server confirmed.
          latestRef.current = savedRef.current;
          setEnabledIds(savedRef.current.enabledIds);
          setCustomServices(savedRef.current.customServices);
          dirtyRef.current = false;
        }
      } while (dirtyRef.current);
      pendingIdsRef.current = new Set();
      setPendingIds(new Set());
    })();
    inFlightRef.current = run;
    return run.finally(() => { inFlightRef.current = null; });
  }

  /** Save shortly after the last tap so a burst of taps becomes one request. */
  function scheduleSave() {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      void runSave();
    }, SAVE_DEBOUNCE_MS);
  }

  /** Save now (and wait for it): used when the caller needs the result. */
  function flushSave(): Promise<void> {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    return runSave();
  }

  // Don't lose a tap that is still waiting on the debounce when leaving the page.
  useEffect(() => () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      void runSave();
    }
  }, []);

  /** Apply a local change optimistically and queue the save. */
  function applyChange(next: ScopeState, touchedId: string) {
    latestRef.current = next;
    setEnabledIds(next.enabledIds);
    setCustomServices(next.customServices);
    pendingIdsRef.current.add(touchedId);
    setPendingIds(new Set(pendingIdsRef.current));
    setSaveError('');
    scheduleSave();
  }

  /** Toggle a service on/off; the save is coalesced with any other quick taps. */
  function handleToggle(serviceId: string) {
    if (quote?.quoteStatus !== 'draft') return;

    const nextIds = new Set(latestRef.current.enabledIds);
    if (nextIds.has(serviceId)) nextIds.delete(serviceId);
    else nextIds.add(serviceId);

    applyChange({ enabledIds: nextIds, customServices: latestRef.current.customServices }, serviceId);
  }

  /** Toggle a custom service on/off. */
  function handleCustomToggle(serviceId: string) {
    if (quote?.quoteStatus !== 'draft') return;

    const nextCustom = latestRef.current.customServices.map(s =>
      s.serviceId === serviceId ? { ...s, enabled: !s.enabled } : s,
    );

    applyChange({ enabledIds: latestRef.current.enabledIds, customServices: nextCustom }, serviceId);
  }

  /** Add a new custom service. */
  async function handleAddCustomService(input: { name: string; party: Party; note?: string }) {
    const newItem: QuoteServiceItem = {
      serviceId: `custom_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      serviceName: input.name,
      party: input.party,
      order: 9999,
      enabled: true,
      isCustom: true,
      note: input.note,
    };
    const nextCustom = [...latestRef.current.customServices, newItem];
    applyChange({ enabledIds: latestRef.current.enabledIds, customServices: nextCustom }, newItem.serviceId);
    await flushSave();
    // The queue reverts to the last confirmed state on failure; tell the form.
    if (!savedRef.current.customServices.some(s => s.serviceId === newItem.serviceId)) {
      throw new Error('Failed to save service.');
    }
  }

  /** Delete a custom service. */
  function handleDeleteCustomService(serviceId: string) {
    if (quote?.quoteStatus !== 'draft') return;

    const nextCustom = latestRef.current.customServices.filter(s => s.serviceId !== serviceId);
    applyChange({ enabledIds: latestRef.current.enabledIds, customServices: nextCustom }, serviceId);
  }

  const loading = quoteLoading || (servicesLoading && !initialized);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <p className="text-small text-rose font-semibold">Quote not found</p>
      </div>
    );
  }

  const isDraft = quote.quoteStatus === 'draft';
  const isResidential = quote?.type === 'residential';

  // Filter services by quote type, then group and sort by party
  const filteredServices = orgServices.filter(s => {
    const svcType = s.quoteType ?? 'commercial';
    return isResidential ? svcType === 'residential' : svcType === 'commercial';
  });

  // Apply enabled-only filter to org services
  const displayedOrgServices = showEnabledOnly
    ? filteredServices.filter(s => enabledIds.has(s.id))
    : filteredServices;

  const customerServices = displayedOrgServices
    .filter(s => s.party === 'Customer')
    .sort((a, b) => a.order - b.order);
  const moverServices = displayedOrgServices
    .filter(s => s.party === 'Mover')
    .sort((a, b) => a.order - b.order);

  // Custom services grouped by party (apply enabled-only filter)
  const displayedCustomServices = showEnabledOnly
    ? customServices.filter(s => s.enabled)
    : customServices;
  const customCustomerServices = displayedCustomServices.filter(s => s.party === 'Customer');
  const customMoverServices = displayedCustomServices.filter(s => s.party === 'Mover');

  const enabledCount = enabledIds.size + customServices.filter(s => s.enabled).length;

  return (
    <div className="px-4 pt-6 pb-10 md:max-w-4xl md:mx-auto">
      {/* Back — hidden on md+ (shell left panel provides navigation) */}
      <button
        type="button"
        onClick={() => navigate(`/moves/${quoteId}`)}
        className="md:hidden flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        {quote.customerCompanyName}
      </button>

      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display text-h1 text-midnight">Scope of Services</h1>
        <div className="flex items-center gap-2">
          {pendingIds.size > 0 && <LoaderCircle size={16} className="text-slate animate-spin shrink-0" />}
          {isDraft && (
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              className="shrink-0 h-9 w-9 rounded-xl bg-plum text-white
                         flex items-center justify-center
                         hover:bg-rose active:opacity-80
                         transition-colors duration-150"
              aria-label="Add custom service"
            >
              <Plus size={18} />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mb-5">
        <p className="text-[13px] text-slate">
          {enabledCount > 0
            ? `${enabledCount} service${enabledCount === 1 ? '' : 's'} selected`
            : '\u00A0'}
        </p>
        {enabledCount > 0 && (
          <button
            type="button"
            onClick={() => setShowEnabledOnly(v => !v)}
            className={`flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1.5 rounded-lg
              transition-colors duration-150
              ${showEnabledOnly
                ? 'bg-plum/10 text-plum'
                : 'text-slate hover:text-midnight hover:bg-mist'
              }`}
          >
            <Filter size={13} />
            Enabled only
          </button>
        )}
      </div>

      {!isDraft && <ReadOnlyBanner status={quote.quoteStatus} />}

      {saveError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{saveError}</p>
        </div>
      )}

      <div className={!isDraft ? 'pointer-events-none opacity-60 select-none' : ''}>
      {filteredServices.length === 0 && customServices.length === 0 ? (
        <div className="flex flex-col items-center py-20 gap-4 text-center">
          <p className="text-small text-slate font-medium">No services configured yet.</p>
          <p className="text-[13px] text-slate leading-snug max-w-xs">
            An admin can add services in the organization settings, or add a custom service for this quote.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-5 md:grid md:grid-cols-2 md:gap-6 md:items-start">

          {/* Customer services */}
          {(customerServices.length > 0 || customCustomerServices.length > 0) && (
            <section>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2.5 px-1">
                Customer
              </p>
              <ul className="flex flex-col gap-2">
                {customerServices.map(svc => (
                  <li key={svc.id}>
                    <ServiceToggleRow
                      service={svc}
                      enabled={enabledIds.has(svc.id)}
                      onToggle={() => handleToggle(svc.id)}
                      saving={pendingIds.has(svc.id)}
                    />
                  </li>
                ))}
                {customCustomerServices.map(item => (
                  <li key={item.serviceId}>
                    <CustomServiceToggleRow
                      item={item}
                      onToggle={() => handleCustomToggle(item.serviceId)}
                      onDelete={() => handleDeleteCustomService(item.serviceId)}
                      saving={pendingIds.has(item.serviceId)}
                      isDraft={isDraft}
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Mover services */}
          {(moverServices.length > 0 || customMoverServices.length > 0) && (
            <section>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2.5 px-1">
                Mover
              </p>
              <ul className="flex flex-col gap-2">
                {moverServices.map(svc => (
                  <li key={svc.id}>
                    <ServiceToggleRow
                      service={svc}
                      enabled={enabledIds.has(svc.id)}
                      onToggle={() => handleToggle(svc.id)}
                      saving={pendingIds.has(svc.id)}
                    />
                  </li>
                ))}
                {customMoverServices.map(item => (
                  <li key={item.serviceId}>
                    <CustomServiceToggleRow
                      item={item}
                      onToggle={() => handleCustomToggle(item.serviceId)}
                      onDelete={() => handleDeleteCustomService(item.serviceId)}
                      saving={pendingIds.has(item.serviceId)}
                      isDraft={isDraft}
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}

        </div>
      )}
      </div>

      {/* Add custom service form */}
      {showAddForm && (
        <AddCustomServiceForm
          onClose={() => setShowAddForm(false)}
          onSave={handleAddCustomService}
        />
      )}
    </div>
  );
}
